import { AxiosError } from 'axios'
import { GraphQLError } from 'graphql'
import { Required } from 'utility-types'
import { FieldValidationError } from './FieldValidationError'
import { GitHubTokenValidationError } from './GitHubTokenValidationError'
import { GraphQLResponseError } from './GraphQLResponseError'
import { JiraPermissionsValidationError } from './JiraPermissionsValidationError'
import { NotFoundError } from './NotFoundError'

/**
 * `isAxiosError` is a type guard similar to `axios.isAxiosError`
 * but it also allows to define response data type using type parameter
 */
export const isAxiosError = <T = unknown>(
  error: unknown
): error is AxiosError<T> => (error as AxiosError)?.isAxiosError

/**
 * `isServerError` returns true when the error is an Axios error whose
 * response status is in the 5xx range.
 */
export const isServerError = (error: unknown): boolean => {
  if (!isAxiosError(error)) {
    return false
  }
  const status = error.response?.status
  return typeof status === 'number' && status >= 500 && status < 600
}

const RETRYABLE_NETWORK_CODES = new Set([
  'ECONNRESET',
  'ECONNABORTED',
  'ETIMEDOUT',
  'ENOTFOUND',
  'EAI_AGAIN',
  'ERR_NETWORK',
])

const RETRYABLE_STATUS_CODES = new Set([502, 503, 504])

/**
 * `isRetryableError` classifies errors that are safe to retry:
 * transient network failures and 502/503/504 upstream errors.
 * 4xx and 500 are treated as non-retryable.
 */
export const isRetryableError = (error: unknown): boolean => {
  if (!isAxiosError(error)) {
    return false
  }
  const status = error.response?.status
  if (typeof status === 'number') {
    return RETRYABLE_STATUS_CODES.has(status)
  }
  // No response: transient network error
  if (error.code && RETRYABLE_NETWORK_CODES.has(error.code)) {
    return true
  }
  return !error.response
}

/**
 * `isAxiosError` is a type guard similar to `axios.isAxiosError`
 * but it also allows to define response data type using type parameter
 * and verifies that `error.response` is not empty.
 */
export const isAxiosErrorWithResponse = <T = unknown>(
  error: unknown
): error is Required<AxiosError<T>, 'response'> =>
  isAxiosError<T>(error) && !!error.response?.status && !!error.response?.data

export const isEmptyGraphError = (error: unknown) => {
  if (isAxiosError<{ error?: { message: string } }>(error)) {
    return error.response?.data?.error?.message === 'empty graph'
  }
  return false
}

export const isNoTeamsAvailableError = (error: unknown) => {
  if (isAxiosError<{ errors: Array<GraphQLError> }>(error)) {
    const [err] = error?.response?.data?.errors ?? []
    return err?.extensions?.code === 'GRAPHQL_VALIDATION_FAILED'
  }
  return false
}

/**
 * `isFieldValidationError` is a type guard to determine
 * if argument is instance of `FieldValidationError`
 */
export const isFieldValidationError = (
  error: unknown
): error is FieldValidationError =>
  (error as FieldValidationError)?.isFieldValidationError

/**
 * `isFieldValidationError` is a type guard to determine
 * if argument is instance of `GitHubTokenValidationError`
 */
export const isGitHubScopeValidationError = (
  error: unknown
): error is GitHubTokenValidationError =>
  (error as GitHubTokenValidationError)?.isGitHubTokenValidationError

/**
 * `isFieldValidationError` is a type guard to determine
 * if argument is instance of `JiraPermissionsValidationError`
 */
export const isJiraPermissionsValidationError = (
  error: unknown
): error is JiraPermissionsValidationError =>
  (error as JiraPermissionsValidationError)?.isJiraPermissionsValidationError

/**
 * `isGraphQLResponseError` is a type guard to determine
 * if argument is instance of `GraphQLResponseError`
 */
export const isGraphQLResponseError = (
  error: unknown
): error is GraphQLResponseError =>
  (error as GraphQLResponseError)?.isGraphQLResponseError

/**
 * `isNotFoundError` is a type guard to determine
 * if argument is instance of `FieldValidationError`
 */
export const isNotFoundError = (error: unknown): error is NotFoundError =>
  (error as NotFoundError)?.isNotFoundError
